// selectionBus.ts — cross-engine selection sync bus.
//
// Per APPLICATION_PATH/design/20-design/interaction-designer/selection-model.md:
// every viewer/engine consumes the same selection through this bus. Engines
// publish selection changes via `publishSelection(...)`; consumers subscribe
// via `useSelection()` (hook) or `selectionBus.subscribe(fn)` (imperative).
//
// The bus is intentionally framework-agnostic at its core (publish-subscribe
// over a local emitter) so engine adapters can use it without React.

export interface SelectionFeature {
  /** Layer id producing the selection. */
  layerId: string;
  /** Feature id within the layer. */
  featureId: string;
  /** Optional opaque payload (engine-side metadata). */
  metadata?: Record<string, unknown>;
}

export interface SelectionEvent {
  features: ReadonlyArray<SelectionFeature>;
  /** Source engine — useful for cross-engine routing. */
  source: 'maplibre' | 'deckgl' | 'cesium' | 'three' | 'plugin' | 'host';
  /** Optional timestamp (epoch ms). */
  ts?: number;
}

type Listener = (event: SelectionEvent) => void;

class SelectionBus {
  private listeners = new Set<Listener>();
  private last: SelectionEvent | null = null;

  publish(event: SelectionEvent): void {
    this.last = event;
    for (const fn of this.listeners) fn(event);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    if (this.last) fn(this.last);
    return () => {
      this.listeners.delete(fn);
    };
  }

  getSnapshot(): SelectionEvent | null {
    return this.last;
  }

  reset(): void {
    this.last = null;
    this.listeners.clear();
  }
}

export const selectionBus = new SelectionBus();

// React hook layered on top of the bus, using use-sync-external-store
// semantics (Phase-2 architecture §2.2). Implemented inline against React
// to avoid the extra dep until use-sync-external-store lands.
import { useEffect, useState } from 'react';

export function useSelection(): SelectionEvent | null {
  const [snapshot, setSnapshot] = useState<SelectionEvent | null>(() => selectionBus.getSnapshot());
  useEffect(() => selectionBus.subscribe(setSnapshot), []);
  return snapshot;
}
